import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, Link } from 'react-router-dom';
import { Box, CssBaseline, AppBar, Toolbar, Typography, Drawer, List, ListItem, ListItemIcon, ListItemText, Button, ListItemButton, Collapse } from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import DashboardIcon from '@mui/icons-material/Dashboard';
import QuizIcon from '@mui/icons-material/Quiz';
import EventIcon from '@mui/icons-material/Event';
import CodeIcon from '@mui/icons-material/Code';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import NotificationsPage from './pages/NotificationsPage';
import DashboardPage from './pages/DashboardPage';
import LoginPage from './pages/LoginPage';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import GeneralNotificationPage from './pages/GeneralNotificationPage';
import QuizNotificationPage from './pages/QuizNotificationPage';
import EventNotificationPage from './pages/EventNotificationPage';
import HackathonNotificationPage from './pages/HackathonNotificationPage';
import AutomatedNotificationsPage from './pages/AutomatedNotificationsPage';
import AnalyticsPage from './pages/AnalyticsPage';
import HackathonAnalyticsPage from './pages/HackathonAnalyticsPage';
import QuizAnalyticsPage from './pages/QuizAnalyticsPage';
import EventAnalyticsPage from './pages/EventAnalyticsPage';
import HackathonDetailPage from './pages/HackathonDetailPage';
import EventDetailPage from './pages/EventDetailPage';
import QuizDetailPage from './pages/QuizDetailPage';
import GeneralSponsors from './pages/GeneralSponsors';
import UserAnalyticsPage from './pages/UserAnalyticsPage';
import AdminPaymentDashboard from './pages/AdminPaymentDashboard';
import ReferralDashboardPage from './pages/ReferralDashboardPage';
import ManageJobsPage from './pages/ManageJobsPage';
import PartnershipsPage from './pages/PartnershipsPage';

const drawerWidth = 220;

const theme = createTheme({
  palette: {
    primary: { main: '#0a66c2' },
    background: { default: '#f3f6f8' },
  },
  typography: {
    fontFamily: 'Segoe UI, Arial, sans-serif',
  },
});

const menuItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
  { text: 'User Analytics', icon: <DashboardIcon />, path: '/user-analytics' },
  { text: 'Growth & Referrals', icon: <EventIcon />, path: '/referrals-dashboard' },
  { text: 'Hackathon Analytics', icon: <QuizIcon />, path: '/hackathon-analytics' },
    { text: 'Hackathon pay', icon: <QuizIcon />, path: '/hackathons-pay' },
  { text: 'Quiz Analytics', icon: <QuizIcon />, path: '/quiz-analytics' },
  { text: 'Event Analytics', icon: <EventIcon />, path: '/event-analytics' },
  { text: 'Sponsor Dashboard', icon: <DashboardIcon />, path: '/general-sponsors' },
  { text: 'Partnerships', icon: <EventIcon />, path: '/partnerships' },
  { text: 'Analytics', icon: <QuizIcon />, path: '/analytics' },
  { text: 'General Notification', icon: <NotificationsIcon />, path: '/general-notification' },
  { text: 'Manage Jobs', icon: <CodeIcon />, path: '/manage-jobs' },
  { text: 'Quiz Notification', icon: <QuizIcon />, path: '/quiz-notification' },
  { text: 'Event Notification', icon: <EventIcon />, path: '/event-notification' },
  { text: 'Hackathon Notification', icon: <CodeIcon />, path: '/hackathon-notification' },
  { text: 'Automated Notifications', icon: <ScheduleIcon />, path: '/automated-notifications' },
];

const MainLayout: React.FC = () => {
  const { isAuthenticated, logout, user } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<Navigate to="/login" />} />
      </Routes>
    );
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: { width: drawerWidth, boxSizing: 'border-box', background: '#fff' },
        }}
      >
        <Toolbar />
        <Box sx={{ overflow: 'auto' }}>
          <List>
            {menuItems.map((item) => (
              <ListItem disablePadding key={item.text}>
                <ListItemButton component={Link} to={item.path} selected={location.pathname === item.path}>
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>
      <Box component="main" sx={{ flexGrow: 1, bgcolor: 'background.default', minHeight: '100vh' }}>
        <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, background: '#0a66c2' }}>
          <Toolbar>
            <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
              Admin Panel
            </Typography>
            {user && (
              <Button color="inherit" onClick={logout} sx={{ textTransform: 'none' }}>
                Logout
              </Button>
            )}
          </Toolbar>
        </AppBar>
        <Toolbar />
        <Box sx={{ p: 3 }}>
          <Routes>
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/general-sponsors" element={<GeneralSponsors />} />
            <Route path="/analytics" element={<AnalyticsPage />} />
            <Route path="/user-analytics" element={<UserAnalyticsPage />} />
            <Route path="/referrals-dashboard" element={<ReferralDashboardPage />} />
            <Route path="/hackathon-analytics" element={<HackathonAnalyticsPage />} />
            <Route path="/hackathons/:id" element={<HackathonDetailPage />} />
            <Route path="/hackathons-pay" element={<AdminPaymentDashboard />} />
            <Route path="/quiz-analytics" element={<QuizAnalyticsPage />} />
            <Route path="/event-analytics" element={<EventAnalyticsPage />} />
            <Route path="/events/:id" element={<EventDetailPage />} />
            <Route path="/quizzes/:id" element={<QuizDetailPage />} />
            <Route path="/general-notification" element={<GeneralNotificationPage />} />
            <Route path="/quiz-notification" element={<QuizNotificationPage />} />
            <Route path="/event-notification" element={<EventNotificationPage />} />
            <Route path="/hackathon-notification" element={<HackathonNotificationPage />} />
            <Route path="/automated-notifications" element={<AutomatedNotificationsPage />} />
            <Route path="/manage-jobs" element={<ManageJobsPage />} />
            <Route path="/partnerships" element={<PartnershipsPage />} />
            <Route path="*" element={<Navigate to="/dashboard" />} />
          </Routes>
        </Box>
      </Box>
    </Box>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <MainLayout />
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
